import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { LeftSide } from '../../components/LeftSide/LeftSide';
import { Navi } from '../../components/Navi/Navi';
import { RightBar } from '../../components/RightBar/RightBar';
import { MdSort, MdArrowDownward, MdArrowUpward } from 'react-icons/md';
import api from '../../utils/api';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import '../../styles/common.css';
import '../Users/User.css';
import { useMinLoadingDisplay } from '../../hooks/useMinLoadingDisplay';
import { orderLimitConfigByMenus } from '../../utils/menuQuotaOrder';

/** 한도 컬럼 최소 너비 — 한글 라벨이 잘리지 않도록 */
function limitColumnWidthPx(label) {
    const len = String(label ?? '').length;
    return Math.min(320, Math.max(168, len * 14 + 64));
}

const QuotaUsageLog = ({ title = '한도 사용량 로그' }) => {
    const [sortKey, setSortKey] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState('');
    const [range, setRange] = useState('today');
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [isLoading, setIsLoading] = useState(false);
    const showTableLoading = useMinLoadingDisplay(isLoading);
    const [isResetting, setIsResetting] = useState(false);
    const [menuCatalogRows, setMenuCatalogRows] = useState([]);
    const [features, setFeatures] = useState([]);

    const orderedLimitConfig = useMemo(
        () => orderLimitConfigByMenus(menuCatalogRows, features.map(f => ({ key: f.queryType, label: f.label, limit: f.defaultLimit }))),
        [menuCatalogRows, features]
    );

    const hMirrorRef = useRef(null);
    const hMainRef = useRef(null);
    const mirrorSizerRef = useRef(null);
    const scrollSyncLock = useRef(false);

    const columnMeta = useMemo(() => {
        const cols = [
            { key: 'login_id', header: '아이디', sortKey: null, width: 132 },
            { key: 'name', header: '이름', sortKey: null, width: 120 },
            ...orderedLimitConfig.map(c => ({
                key: c.key,
                header: c.label,
                sortKey: c.key,
                width: limitColumnWidthPx(c.label),
            })),
            {
                key: 'last_used_at',
                header: '마지막 사용',
                sortKey: 'last_used_at',
                width: 228,
            },
        ];
        return cols;
    }, [orderedLimitConfig]);

    const sortedRows = useMemo(() => {
        if (!sortKey || !sortOrder) return rows;
        const copy = [...rows];
        copy.sort((a, b) => {
            let aValue = a[sortKey] ?? 0;
            let bValue = b[sortKey] ?? 0;
            const aNum = parseFloat(aValue);
            const bNum = parseFloat(bValue);
            const bothNumeric = !isNaN(aNum) && !isNaN(bNum);
            if (bothNumeric) return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
            return sortOrder === 'asc'
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });
        return copy;
    }, [rows, sortKey, sortOrder]);

    const updateMirrorSizerWidth = useCallback(() => {
        const main = hMainRef.current;
        const sizer = mirrorSizerRef.current;
        if (!main || !sizer) return;
        sizer.style.width = `${main.scrollWidth}px`;
    }, []);

    useLayoutEffect(() => {
        updateMirrorSizerWidth();
        const main = hMainRef.current;
        if (!main || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => updateMirrorSizerWidth());
        ro.observe(main);
        return () => ro.disconnect();
    }, [updateMirrorSizerWidth, sortedRows, columnMeta, showTableLoading]);

    useEffect(() => {
        const onWin = () => updateMirrorSizerWidth();
        window.addEventListener('resize', onWin);
        return () => window.removeEventListener('resize', onWin);
    }, [updateMirrorSizerWidth]);

    useLayoutEffect(() => {
        const mirror = hMirrorRef.current;
        const main = hMainRef.current;
        if (!mirror || !main) return undefined;

        const onMirrorScroll = () => {
            if (scrollSyncLock.current) return;
            scrollSyncLock.current = true;
            main.scrollLeft = mirror.scrollLeft;
            requestAnimationFrame(() => {
                scrollSyncLock.current = false;
            });
        };
        const onMainScroll = () => {
            if (scrollSyncLock.current) return;
            scrollSyncLock.current = true;
            mirror.scrollLeft = main.scrollLeft;
            requestAnimationFrame(() => {
                scrollSyncLock.current = false;
            });
        };

        mirror.addEventListener('scroll', onMirrorScroll, { passive: true });
        main.addEventListener('scroll', onMainScroll, { passive: true });
        return () => {
            mirror.removeEventListener('scroll', onMirrorScroll);
            main.removeEventListener('scroll', onMainScroll);
        };
    }, [sortedRows.length, columnMeta.length, showTableLoading]);

    const handleSort = key => {
        if (sortKey !== key) {
            setSortKey(key);
            setSortOrder('desc');
            return;
        }
        if (sortOrder === 'desc') setSortOrder('asc');
        else if (sortOrder === 'asc') {
            setSortKey(null);
            setSortOrder(null);
        } else setSortOrder('desc');
    };

    const renderSortIcon = key => {
        const base = { marginLeft: 6, fontSize: 16, verticalAlign: 'middle', flexShrink: 0 };
        if (sortKey !== key || !sortOrder) return <MdSort style={{ ...base, color: '#bbb' }} />;
        if (sortOrder === 'desc') return <MdArrowDownward style={{ ...base, color: '#000' }} />;
        return <MdArrowUpward style={{ ...base, color: '#000' }} />;
    };

    const updateDateRange = value => {
        let start, end;
        const today = dayjs();

        switch (value) {
            case 'today':
                start = today;
                end = today;
                break;
            case 'thisWeek':
                start = today.startOf('week').add(1, 'day');
                end = today;
                break;
            case 'lastWeek':
                start = today.subtract(1, 'week').startOf('week').add(1, 'day');
                end = today.subtract(1, 'week').endOf('week').add(1, 'day');
                break;
            case 'thisMonth':
                start = today.startOf('month');
                end = today;
                break;
            case 'lastMonth':
                start = today.subtract(1, 'month').startOf('month');
                end = today.subtract(1, 'month').endOf('month');
                break;
            default:
                start = today.startOf('month');
                end = today;
                break;
        }

        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
    };

    const handleStartDateChange = value => {
        if (dayjs(value).isAfter(dayjs(endDate))) {
            toast.warning('시작 날짜는 종료 날짜보다 이후일 수 없습니다.');
            return;
        }
        setStartDate(value);
    };

    const handleEndDateChange = value => {
        if (dayjs(value).isBefore(dayjs(startDate))) {
            toast.warning('종료 날짜는 시작 날짜보다 이전일 수 없습니다.');
            return;
        }
        setEndDate(value);
    };

    async function fetchData() {
        try {
            const diffDays = dayjs(endDate).diff(dayjs(startDate), 'day');
            if (diffDays > 31) {
                toast.warning('조회 기간은 최대 31일까지 가능합니다.');
                return;
            }
            setIsLoading(true);
            const res = await api.get('/quota/usage', {
                params: { search, range, startDate, endDate },
            });
            if (res.data.ok) {
                setRows(res.data.data);
            } else {
                toast.error(
                    res.data.message || '한도 사용량 데이터를 불러오지 못했습니다.'
                );
            }
        } catch (err) {
            console.error(err);
            toast.error('데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    }

    const handleReset = () => {
        setSearch('');
        setRange('today');
        updateDateRange('today');
        setIsResetting(true);
    };

    useEffect(() => {
        if (isResetting) {
            fetchData();
            setIsResetting(false);
        }
    }, [range, startDate, endDate, isResetting]);

    useEffect(() => {
        (async () => {
            try {
                const [menuRes, featureRes] = await Promise.all([
                    api.get('/settings/menus'),
                    api.get('/settings/features'),
                ]);
                if (menuRes.data?.success && Array.isArray(menuRes.data.data)) {
                    setMenuCatalogRows(menuRes.data.data);
                }
                if (featureRes.data?.success && Array.isArray(featureRes.data.data)) {
                    setFeatures(featureRes.data.data);
                }
            } catch (e) {
                console.warn('메뉴/기능 목록 로드 실패:', e);
            }
        })();
    }, []);

    useEffect(() => {
        fetchData();
    }, []);

    const handleExcelDownload = () => {
        if (!rows.length) {
            toast.warning('다운로드할 데이터가 없습니다.');
            return;
        }
        const exportData = rows.map(r => {
            const row = {
                아이디: r.login_id,
                이름: r.name,
            };
            orderedLimitConfig.forEach(({ key, label }) => {
                row[label] = r[key] ?? 0;
            });
            row['마지막 사용'] = r.last_used_at?.replace('T', ' ');
            return row;
        });
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '한도 사용량 로그');
        XLSX.writeFile(wb, `한도_사용량_로그_${dayjs().format('YYYY-MM-DD_HHmmss')}.xlsx`);
    };

    const showMirror = !showTableLoading && sortedRows.length > 0;

    return (
        <>
            <div id="wrapper">
                <Navi title={title} />
                <LeftSide />

                <div className="content-page">
                    <div className="content">
                        <div className="container-fluid users-admin-page">
                            <div className="row">
                                <div className="col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-3">
                                                <div className="d-flex flex-wrap align-items-center gap-1">
                                                    <select
                                                        className="form-select form-select-sm"
                                                        style={{ width: 130 }}
                                                        value={range}
                                                        onChange={e => {
                                                            setRange(e.target.value);
                                                            updateDateRange(e.target.value);
                                                        }}
                                                    >
                                                        <option value="today">오늘</option>
                                                        <option value="thisWeek">이번주</option>
                                                        <option value="lastWeek">지난주</option>
                                                        <option value="thisMonth">이번달</option>
                                                        <option value="lastMonth">지난달</option>
                                                    </select>

                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        value={startDate}
                                                        onChange={e =>
                                                            handleStartDateChange(e.target.value)
                                                        }
                                                        style={{ width: 140 }}
                                                    />
                                                    <span>~</span>
                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        value={endDate}
                                                        onChange={e =>
                                                            handleEndDateChange(e.target.value)
                                                        }
                                                        style={{ width: 140 }}
                                                    />

                                                    <input
                                                        className="form-control form-control-sm"
                                                        placeholder="사용자명 또는 아이디"
                                                        value={search}
                                                        onChange={e => setSearch(e.target.value)}
                                                        style={{ width: 220 }}
                                                    />
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => fetchData()}
                                                        disabled={isLoading}
                                                    >
                                                        <i className="fas fa-search me-1"></i>
                                                        {isLoading ? '조회 중...' : '검색'}
                                                    </button>

                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={handleReset}
                                                        disabled={isLoading}
                                                    >
                                                        <i className="mdi mdi-refresh me-1"></i>{' '}
                                                        초기화
                                                    </button>
                                                </div>
                                                <div>
                                                    <button
                                                        className="btn btn-success btn-sm"
                                                        onClick={handleExcelDownload}
                                                        disabled={isLoading}
                                                    >
                                                        <i className="mdi mdi-download me-1"></i>{' '}
                                                        엑셀 다운로드
                                                    </button>
                                                </div>
                                            </div>

                                            {showTableLoading ? (
                                                <div className="text-center p-4">
                                                    <div
                                                        className="loading-spinner loading-spinner-lg"
                                                        role="status"
                                                        aria-live="polite"
                                                    >
                                                        <span className="visually-hidden">
                                                            데이터를 불러오는 중입니다.
                                                        </span>
                                                    </div>
                                                    <p className="mt-2">
                                                        데이터를 불러오는 중입니다...
                                                    </p>
                                                </div>
                                            ) : sortedRows.length === 0 ? (
                                                <div className="text-center text-muted py-4">
                                                    데이터가 없습니다.
                                                </div>
                                            ) : (
                                                <div className="quota-usage-log-table-block">
                                                    <div
                                                        ref={hMainRef}
                                                        className="quota-usage-log-hscroll-main"
                                                    >
                                                        <table className="table table-bordered table-hover table-sm align-middle quota-usage-log-table">
                                                            <colgroup>
                                                                {columnMeta.map(c => (
                                                                    <col
                                                                        key={c.key}
                                                                        style={{
                                                                            width: c.width,
                                                                            minWidth: c.width,
                                                                        }}
                                                                    />
                                                                ))}
                                                            </colgroup>
                                                            <thead className="table-light text-center">
                                                                <tr>
                                                                    {columnMeta.map(c => {
                                                                        const sortable =
                                                                            c.sortKey != null;
                                                                        return (
                                                                            <th
                                                                                key={c.key}
                                                                                scope="col"
                                                                                className={
                                                                                    sortable
                                                                                        ? 'quota-usage-log-th-sort'
                                                                                        : undefined
                                                                                }
                                                                                onClick={
                                                                                    sortable
                                                                                        ? () =>
                                                                                              handleSort(
                                                                                                  c.sortKey
                                                                                              )
                                                                                        : undefined
                                                                                }
                                                                            >
                                                                                {sortable ? (
                                                                                    <div className="quota-usage-log-th-inner">
                                                                                        <span className="quota-usage-log-th-text">
                                                                                            {
                                                                                                c.header
                                                                                            }
                                                                                        </span>
                                                                                        {renderSortIcon(
                                                                                            c.sortKey
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    c.header
                                                                                )}
                                                                            </th>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            </thead>
                                                            <tbody className="text-center">
                                                                {sortedRows.map(r => (
                                                                    <tr key={r.user_id}>
                                                                        <td className="users-admin-col-id">
                                                                            {r.login_id}
                                                                        </td>
                                                                        <td>{r.name}</td>
                                                                        {orderedLimitConfig.map(
                                                                            ({ key }) => (
                                                                                <td key={key}>
                                                                                    {r[key] ?? 0}
                                                                                </td>
                                                                            )
                                                                        )}
                                                                        <td className="quota-usage-log-cell-time">
                                                                            {r.last_used_at?.replace(
                                                                                'T',
                                                                                ' '
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    {showMirror && (
                                                        <div className="quota-usage-log-hscroll-sticky">
                                                            <div
                                                                ref={hMirrorRef}
                                                                className="quota-usage-log-hscroll-mirror"
                                                                tabIndex={0}
                                                                title="표와 연동된 가로 스크롤 (화면 하단 고정)"
                                                                aria-label="표 가로 스크롤, 본문과 동기화"
                                                            >
                                                                <div
                                                                    ref={mirrorSizerRef}
                                                                    className="quota-usage-log-hscroll-mirror-sizer"
                                                                    aria-hidden
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <RightBar />
            <div className="rightbar-overlay"></div>
        </>
    );
};

export { QuotaUsageLog };
